import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';

const AxoHakemusPage = () => {
  console.log('🔥 AxoHakemusPage rendering');
  
  const [searchParams] = useSearchParams();

  // Get parameters from URL
  const loanAmount = searchParams.get('amount');
  const loanTerm = searchParams.get('term');
  const loanPurpose = searchParams.get('purpose');
  
  console.log('✅ URL parameters:', { loanAmount, loanTerm, loanPurpose });

  
  useEffect(() => {
    // Aseta parametrit globaaleiksi muuttujiksi - nämä voidaan lukea Axo scriptissä
    if (loanAmount) (window as any).loanAmount = loanAmount;
    if (loanTerm) (window as any).loanTerm = loanTerm;
    if (loanPurpose) (window as any).loanPurpose = loanPurpose;
    
    console.log('🚀 Setting up Axo script with params:', { loanAmount, loanTerm, loanPurpose });
    
    // Poista aiempi script jos on
    const existingScript = document.getElementById('axo-script');
    if (existingScript) {
      console.log('🗑️ Removing existing script');
      existingScript.remove();
    }
    
    // Varmista että body on tyhjä ennen scriptin lataamista
    const bodyContent = document.body.innerHTML;
    console.log('📄 Current body content length:', bodyContent.length);
    
    // Lataa Axo script suoraan bodyyn dokumentaation mukaan
    const script = document.createElement('script');
    script.id = 'axo-script';
    script.src = 'https://embed.axogroup.com/axolaina.fi/loan-application/main.js';
    script.setAttribute('data-has-offers-affiliate-url', 'https://go.axolaina.fi/aff_c?offer_id=820&aff_id=1657');
    script.type = 'module';
    
    script.onload = () => {
      console.log('✅ Axo script loaded successfully');
      console.log('🔍 Checking DOM for Axo elements...');
      setTimeout(() => {
        const axoElements = document.querySelectorAll('[id*="axo"], [class*="axo"]');
        console.log('🎯 Found Axo elements:', axoElements.length);
        axoElements.forEach((el, i) => {
          console.log(`Element ${i}:`, el.tagName, el.id, el.className);
        });
      }, 1000);
    };
    
    script.onerror = (error) => {
      console.error('❌ Axo script failed to load:', error);
    };
    
    console.log('📥 Appending script to document head');
    document.head.appendChild(script);
    
    return () => {
      const scriptToRemove = document.getElementById('axo-script');
      if (scriptToRemove) {
        console.log('🧹 Cleanup: removing script');
        scriptToRemove.remove();
      }
    };
  }, [loanAmount, loanTerm, loanPurpose]);

  return (
    <>
      <Helmet>
        <title>Lainahakemus | Lainasampo</title>
        <meta name="description" content="Täytä lainahakemus helposti. Nopea käsittely ja kilpailukykyiset korot." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100" style={{ scrollBehavior: 'smooth' }}>
        <div className="container mx-auto px-4 py-4">
          {/* Header - sticky positioning */}
          <div className="sticky top-0 z-10 bg-gradient-to-br from-blue-50 to-indigo-100 pb-4 text-center mb-4">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Hae lainaa helposti
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Täytä hakemus alla ja saat vastauksen nopeasti. Luotettava palvelu.
            </p>
          </div>


          {/* AXO Lomake iframe */}
          <div className="flex gap-6">
            {/* Sivupalkki tietokoneäkymässä */}
            <div className="hidden lg:block w-72 space-y-3">
              <div className="bg-green-50 border border-green-200 p-3 rounded-lg">
                <h3 className="font-semibold text-green-800 mb-1 text-sm">✅ SSL-suojattu</h3>
                <p className="text-xs text-green-700">Tietosi käsitellään suojauksen takana</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
                <h3 className="font-semibold text-blue-800 mb-1 text-sm">⚡ 15 min vastaus</h3>
                <p className="text-xs text-blue-700">Saat lainapäätöksen jopa 15 minuutissa</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
                <h3 className="font-semibold text-amber-800 mb-1 text-sm">🏦 Yli 50k asiakasta</h3>
                <p className="text-xs text-amber-700">Tyytyväisiä asiakkaita Suomessa</p>
              </div>
              <div className="bg-purple-50 border border-purple-200 p-3 rounded-lg">
                <h3 className="font-semibold text-purple-800 mb-1 text-sm">💯 Ilmainen vertailu</h3>
                <p className="text-xs text-purple-700">Ei piilotettuja kuluja tai maksuja</p>
              </div>
            </div>

          {/* Lomake - iframe joka lataa suoraan axo-form.html */}
          <div className="flex-1 bg-white rounded-lg shadow-lg overflow-hidden">
            <iframe 
              src={`/axo-form.html?amount=${loanAmount || ''}&term=${loanTerm || ''}&purpose=${loanPurpose || ''}`}
              className="w-full h-[800px] border-0"
              title="Lainahakemus"
              allowFullScreen
            />
          </div>
          </div>

          {/* Info section */}
          <div className="mt-8 max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg p-6 text-center shadow-md">
                <div className="text-blue-600 text-3xl mb-3">⚡</div>
                <h3 className="font-semibold text-gray-800 mb-2">Nopea käsittely</h3>
                <p className="text-sm text-gray-600">Saat vastauksen hakemukseesi nopeasti</p>
              </div>
              
              <div className="bg-white rounded-lg p-6 text-center shadow-md">
                <div className="text-blue-600 text-3xl mb-3">🔒</div>
                <h3 className="font-semibold text-gray-800 mb-2">Turvallinen</h3>
                <p className="text-sm text-gray-600">Tietosi käsitellään turvallisesti</p>
              </div>
              
              <div className="bg-white rounded-lg p-6 text-center shadow-md">
                <div className="text-blue-600 text-3xl mb-3">💰</div>
                <h3 className="font-semibold text-gray-800 mb-2">Kilpailukykyiset korot</h3>
                <p className="text-sm text-gray-600">Saat parhaan mahdollisen tarjouksen</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AxoHakemusPage;